/**
 * @module components/Checkout/InputGroups
 * @description Grouped form input sections for checkout form.
 * Organizes related fields into Required, AdditionalInfo, and AuthorizationID groups.
 */

import React from 'react';

import {
  Field,
  IntlPhoneInput,
  // CountrySelect,
  TerritorySelect,
} from './Inputs';
import { useField } from './useCheckoutForm';
import AuthorizationIDField from './AuthorizationIDField';
import Accordion from '../Accordion';

/**
 * @component Required
 * @type {React.FC}
 * @summary Required information section with name, email, company, country, and client ID fields.
 * All fields in this group must be filled before checkout can proceed.
 * @returns {React.ReactElement} Required fields group
 */
export const Required = () => (
  <>
    <div className="mb05 semi akz-e py025 al s22">Required Information</div>

    <div className="pb05">
      <div className="x input__row">
        <Field field="name" label="Name*" required />
        <Field field="email" label="Email*" required disabled />
      </div>

      <div className="x input__row">
        <Field field="company" label="Company*" required disabled />
        {/* <Field field="country" label="Country*" required>
          <CountrySelect />
        </Field> */}
        <Field
          field="reference"
          label="Reference (PO number)"
        />
      </div>

      {/* <Field
        field="companyId"
        label="Client ID*"
        required
        className="company-id"
      /> */}

        <Field field="note" label="Note" type="textarea" />
    </div>
  </>
);

/**
 * @component AdditionalInfo
 * @type {React.FC}
 * @summary form/JSX block for additional information fields
 * @returns {ReactNode} group of fields for additional information
 */
export const AdditionalInfo = () => {
  const { value: country } = useField('country');
  const provinceLabel = country === 'US' ? 'State' : 'Province';

  return (
    <Accordion
      label="Additional Information (optional)"
      labelClass="mb075 al s22"
      className="al"
    >
      <div className="pb1">
        <div className="x f input__row">
          <Field field="street" label="Address" />
          <Field
            field="street2"
            label="Apartment/Suite"
            className="input__small"
          />
        </div>

        <div className="x f input__row">
          <Field field="city" label="City" />
          <Field field="zip" label="Zip" className="input__small" />
        </div>

        <div className="x f input__row">
          <Field field="province" label={provinceLabel} />
          <Field
            field="territory"
            label="Territory"
            className="input__small territory"
          >
            <TerritorySelect />
          </Field>
        </div>

      <div className="x input__row">
        <Field field="phone" label="Phone">
          <IntlPhoneInput field="phone" placeholder="Phone" />
        </Field>
        {/*     <Field field="cell" label="Cell"> */}
        {/*       <IntlPhoneInput field="cell" placeholder="Cell" /> */}
        {/*     </Field> */}
        <Field field="fax" label="Fax">
          <IntlPhoneInput field="fax" placeholder="Fax" />
        </Field>
      </div>

      <div className="x input__row">
        <Field field="website" label="Website" />
        <Field
          field="reference"
          label="Reference (PO number)"
          className="input__reference"
        />
      </div>

        <Field field="note" label="Note" type="textarea" />
      </div>
    </Accordion>
  );
};

/**
 * @component AuthorizationID
 * @type {React.FC}
 * @summary form/JSX block with authorization id field
 * @returns {ReactNode} accordion with authorization id field
 */
export const AuthorizationID = () => (
  <Accordion
    label="Authorization ID (optional)"
    labelClass="mb075 al s22 block"
    className="al inline-block"
  >
    <div className="pb1">
      <AuthorizationIDField />
    </div>
  </Accordion>
);
